import React, { useState, useEffect } from "react";
import { X, ChevronDown } from "lucide-react";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import type { ImoveisFilters } from "@/hooks/useImoveis";

interface FilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  currentFilters?: ImoveisFilters;
  onApplyFilters: (filters: ImoveisFilters) => void;
  availableCount?: number;
}

export default function FilterDrawer({ 
  isOpen, 
  onClose, 
  currentFilters, 
  onApplyFilters, 
  availableCount 
}: FilterDrawerProps) {
  const [activeToggle, setActiveToggle] = useState("Todos");
  const [selectedPropertyTypes, setSelectedPropertyTypes] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedPropertyFeatures, setSelectedPropertyFeatures] = useState<string[]>([]);
  const [selectedCondominiumFeatures, setSelectedCondominiumFeatures] = useState<string[]>([]);
  const [selectedPadrao, setSelectedPadrao] = useState<string>("");
  const [quartos, setQuartos] = useState("0");
  const [suites, setSuites] = useState("0");
  const [banheiros, setBanheiros] = useState("0");
  const [vagas, setVagas] = useState("0");
  const [areaMin, setAreaMin] = useState("");
  const [areaMax, setAreaMax] = useState("");
  const [valorMin, setValorMin] = useState("");
  const [valorMax, setValorMax] = useState("");

  // Mapeamento de tipos por categoria
  const TIPOS_POR_CATEGORIA: Record<string, string[]> = {
    'Residencial': ['Casa', 'Apartamento', 'Sobrado', 'Cobertura', 'Studio', 'Casa de Condomínio', 'Loft'],
    'Comercial': ['Sala Comercial', 'Loja', 'Galpão', 'Prédio Comercial', 'Terreno Comercial'],
    'Rural': ['Fazenda', 'Sítio', 'Chácara', 'Terreno Rural'],
  };

  // Tipos disponíveis baseados na categoria selecionada
  const tiposDisponiveis = selectedCategories.length === 1
    ? TIPOS_POR_CATEGORIA[selectedCategories[0]] || []
    : ['Casa', 'Apartamento', 'Sobrado', 'Cobertura', 'Studio', 'Casa de Condomínio', 'Loft', 'Terreno'];

  // Limpar tipos selecionados se não estiverem disponíveis na categoria atual
  useEffect(() => {
    if (selectedCategories.length === 1) {
      const tiposValidos = TIPOS_POR_CATEGORIA[selectedCategories[0]] || [];
      setSelectedPropertyTypes(prev => 
        prev.filter(tipo => tiposValidos.includes(tipo))
      );
    }
  }, [selectedCategories]);

  // Bloquear scroll do body quando drawer está aberto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Sincronizar com filtros externos
  useEffect(() => {
    if (currentFilters && isOpen) {
      setActiveToggle(
        currentFilters.finalidade === 'locacao' ? 'Alugar' :
        currentFilters.finalidade === 'venda' ? 'Comprar' : 'Todos'
      );
      if (currentFilters.tipo && currentFilters.tipo.length > 0) {
        // Mapear TODOS os tipos do banco para labels
        const tipoMap: Record<string, string> = {
          'casa': 'Casa',
          'apartamento': 'Apartamento',
          'studio': 'Studio',
          'sobrado': 'Sobrado',
          'cobertura': 'Cobertura',
          'terreno': 'Terreno',
          'sala_comercial': 'Sala Comercial',
          'loja': 'Loja',
          'galpao': 'Galpão',
          'predio_comercial': 'Prédio Comercial',
          'fazenda': 'Fazenda',
          'sitio': 'Sítio',
          'chacara': 'Chácara',
        };
        const labels = currentFilters.tipo.map(t => tipoMap[t] || t);
        setSelectedPropertyTypes(labels);
      } else {
        setSelectedPropertyTypes([]);
      }
      if (currentFilters.padrao) {
        const padraoMap: Record<string, string> = {
          'alto_padrao': 'Alto Padrão',
          'medio_padrao': 'Médio Padrão',
          'padrao': 'Padrão',
          'economico': 'Econômico',
        };
        setSelectedPadrao(padraoMap[currentFilters.padrao] || '');
      } else {
        setSelectedPadrao('');
      }
      if (currentFilters.quartos_min) {
        const value = currentFilters.quartos_min >= 5 ? '5+' : currentFilters.quartos_min.toString();
        setQuartos(value);
      }
      if (currentFilters.suites_min) {
        setSuites(currentFilters.suites_min >= 5 ? '5+' : currentFilters.suites_min.toString());
      }
      if (currentFilters.banheiros_min) {
        setBanheiros(currentFilters.banheiros_min >= 5 ? '5+' : currentFilters.banheiros_min.toString());
      }
      if (currentFilters.vagas_min) {
        setVagas(currentFilters.vagas_min >= 5 ? '5+' : currentFilters.vagas_min.toString());
      }
      if (currentFilters.area_min) {
        setAreaMin(currentFilters.area_min.toString());
      }
      if (currentFilters.area_max) {
        setAreaMax(currentFilters.area_max.toString());
      }
      if (currentFilters.valor_min) {
        setValorMin(currentFilters.valor_min.toString());
      }
      if (currentFilters.valor_max) {
        setValorMax(currentFilters.valor_max.toString());
      }
      if (currentFilters.categoria) {
        const categoriaMap: Record<string, string> = {
          'residencial': 'Residencial',
          'comercial': 'Comercial',
          'rural': 'Rural',
        };
        const label = categoriaMap[currentFilters.categoria];
        if (label) {
          setSelectedCategories([label]);
        }
      }
    }
  }, [currentFilters, isOpen]);


  const propertyFeatures = [
    "Ar-condicionado",
    "Aceita animais",
    "Varanda Gourmet",
    "Cozinha americana",
    "Closet",
    "Lareira",
    "Piscina",
    "Mobiliado",
  ];

  const condominiumFeatures = [
    "Academia",
    "Salão de Festas",
    "Elevador",
    "Salão de Jogos",
    "Espaço Gourmet",
    "Jardim",
    "Piscina",
    "Playground",
    "Churrasqueira",
    "Quadra de Squash",
    "Quadra de squash",
    "Cinema",
    "Quadra de tênis",
    "Bicicletário",
    "Quadra poliesportiva",
    "Coworking",
    "Acesso para deficientes",
    "Sauna",
    "Lavanderia",
    "Spa",
    "Condomínio fechado",
    "Portaria 24h",
    "Portão eletrônico",
  ];

  const togglePropertyType = (type: string) => {
    setSelectedPropertyTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  };

  const togglePropertyFeature = (feature: string) => {
    setSelectedPropertyFeatures((prev) =>
      prev.includes(feature)
        ? prev.filter((f) => f !== feature)
        : [...prev, feature],
    );
  };

  const toggleCondominiumFeature = (feature: string) => {
    setSelectedCondominiumFeatures((prev) =>
      prev.includes(feature)
        ? prev.filter((f) => f !== feature)
        : [...prev, feature],
    );
  };

  const toggleCategory = (category: string) => {
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category],
    );
  };

  const handleApplyFilters = () => {
    const filters: ImoveisFilters = {};
    
    // Finalidade
    if (activeToggle === 'Alugar') {
      filters.finalidade = 'locacao';
    } else if (activeToggle === 'Comprar') {
      filters.finalidade = 'venda';
    }
    
    // Categoria
    if (selectedCategories.length > 0) {
      const categoriaMap: Record<string, 'residencial' | 'comercial' | 'rural'> = {
        'Residencial': 'residencial',
        'Comercial': 'comercial',
        'Rural': 'rural',
      };
      filters.categoria = categoriaMap[selectedCategories[0]];
    }
    
    // Tipo (mapear TODOS os selecionados e remover duplicatas)
    if (selectedPropertyTypes.length > 0) {
      const tipoMap: Record<string, string> = {
        // Residencial
        'Casa': 'casa',
        'Apartamento': 'apartamento',
        'Sobrado': 'sobrado',
        'Cobertura': 'cobertura',
        'Studio': 'studio',
        'Casa de Condomínio': 'casa',
        'Loft': 'apartamento',
        'Terreno': 'terreno',
        // Comercial
        'Sala Comercial': 'sala_comercial',
        'Loja': 'loja',
        'Galpão': 'galpao',
        'Prédio Comercial': 'predio_comercial',
        'Terreno Comercial': 'terreno',
        // Rural
        'Fazenda': 'fazenda',
        'Sítio': 'sitio',
        'Chácara': 'chacara',
        'Terreno Rural': 'terreno',
      };
      // Mapear todos os tipos e remover duplicatas
      const tiposMapeados = selectedPropertyTypes.map(tipo => tipoMap[tipo] || tipo.toLowerCase());
      filters.tipo = [...new Set(tiposMapeados)];
    }

    // Padrão
    if (selectedPadrao) {
      const padraoMap: Record<string, string> = {
        'Alto Padrão': 'alto_padrao',
        'Médio Padrão': 'medio_padrao',
        'Padrão': 'padrao',
        'Econômico': 'economico',
      };
      filters.padrao = padraoMap[selectedPadrao] as any;
    }

    // Valor mínimo e máximo
    if (valorMin && valorMin.trim() !== '') {
      const parsed = parseInt(valorMin);
      if (!isNaN(parsed) && parsed > 0) {
        filters.valor_min = parsed;
      }
    }
    
    if (valorMax && valorMax.trim() !== '') {
      const parsed = parseInt(valorMax);
      if (!isNaN(parsed) && parsed > 0) {
        filters.valor_max = parsed;
      }
    }
    
    // Características mínimas
    if (quartos !== '0' && quartos !== '5+') {
      filters.quartos_min = parseInt(quartos);
    } else if (quartos === '5+') {
      filters.quartos_min = 5;
    }
    
    if (suites !== '0' && suites !== '5+') {
      filters.suites_min = parseInt(suites);
    } else if (suites === '5+') {
      filters.suites_min = 5;
    }
    
    if (banheiros !== '0' && banheiros !== '5+') {
      filters.banheiros_min = parseInt(banheiros);
    } else if (banheiros === '5+') {
      filters.banheiros_min = 5;
    }
    
    if (vagas !== '0' && vagas !== '5+') {
      filters.vagas_min = parseInt(vagas);
    } else if (vagas === '5+') {
      filters.vagas_min = 5;
    }
    
    // Área
    if (areaMin && areaMin.trim() !== '') {
      const parsed = parseInt(areaMin);
      if (!isNaN(parsed) && parsed > 0) {
        filters.area_min = parsed;
      }
    }
    
    if (areaMax && areaMax.trim() !== '') {
      const parsed = parseInt(areaMax);
      if (!isNaN(parsed) && parsed > 0) {
        filters.area_max = parsed;
      }
    }
    
    // Diferenciais
    if (selectedPropertyFeatures.length > 0) {
      filters.diferenciais = selectedPropertyFeatures;
    }
    
    if (selectedCondominiumFeatures.length > 0) {
      filters.diferenciais_condominio = selectedCondominiumFeatures;
    }
    
    onApplyFilters(filters);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Drawer */}
      <div className="relative z-10 ml-auto h-full w-full max-w-[575px] bg-white flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-neutral-black">Filtros</h2>
          <button onClick={onClose} className="p-1">
            <X className="w-6 h-6 text-neutral-black" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden overscroll-contain p-6 space-y-8 touch-pan-y">
          {/* Property Purpose Toggle */}
          <div className="space-y-3">
            <label className="text-sm text-neutral-black">
              Eu quero encontrar um imóvel para
            </label>
            <div className="flex flex-wrap gap-2">
              {["Todos", "Alugar", "Comprar"].map((option) => (
                <button
                  key={option}
                  onClick={() => setActiveToggle(option)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    activeToggle === option
                      ? "bg-neutral-gray-bg border border-neutral-gray-dark text-neutral-black"
                      : "text-neutral-gray-light border border-gray-200"
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>

          {/* Category Selection */}
          <div className="space-y-4">
            <h3 className="text-sm font-bold text-neutral-black">Categoria</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {["Residencial", "Comercial", "Rural"].map((category) => (
                <label
                  key={category}
                  className="flex items-center space-x-3 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedCategories.includes(category)}
                    onChange={() => toggleCategory(category)}
                    className="w-6 h-6 border-2 border-neutral-black rounded"
                  />
                  <span className="text-base text-neutral-black">{category}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Padrão do Imóvel (apenas residencial) */}
          {selectedCategories.includes('Residencial') && (
            <div className="space-y-4">
              <h3 className="text-sm font-bold text-neutral-black">Padrão do Imóvel</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {['Alto Padrão', 'Médio Padrão', 'Padrão', 'Econômico'].map((padrao) => (
                  <label key={padrao} className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="padrao"
                      checked={selectedPadrao === padrao}
                      onChange={() => setSelectedPadrao(selectedPadrao === padrao ? '' : padrao)}
                      className="w-5 h-5 border-2 border-neutral-black rounded-full cursor-pointer accent-neutral-black"
                    />
                    <span className="text-base text-neutral-black">{padrao}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Property Types */}
          <div className="space-y-4">
            <h3 className="text-sm font-bold text-neutral-black">Tipo</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {tiposDisponiveis.map((type) => (
                <label
                  key={type}
                  className="flex items-center space-x-3 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedPropertyTypes.includes(type)}
                    onChange={() => togglePropertyType(type)}
                    className="w-6 h-6 border-2 border-neutral-black rounded"
                  />
                  <span className="text-base text-neutral-black">{type}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Room Specifications */}
          <div className="grid grid-cols-2 gap-6">
            {[
              { label: "Quartos", value: quartos, setter: setQuartos, key: "quartos" },
              { label: "Suítes", value: suites, setter: setSuites, key: "suites" },
              { label: "Banheiros", value: banheiros, setter: setBanheiros, key: "banheiros" },
              { label: "Vagas", value: vagas, setter: setVagas, key: "vagas" },
            ].map(({ label, value, setter, key }) => (
              <div key={label} className="space-y-2">
                <label className="text-sm font-semibold text-neutral-black">{label} *</label>
                <Select value={value} onValueChange={setter}>
                  <SelectTrigger className="w-full h-12 rounded-full border border-gray-200 bg-white text-neutral-black text-base px-5 focus:outline-none focus:ring-2 focus:ring-neutral-black/10 focus:border-neutral-black transition-all">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0</SelectItem>
                    <SelectItem value="1">1</SelectItem>
                    <SelectItem value="2">2</SelectItem>
                    <SelectItem value="3">3</SelectItem>
                    <SelectItem value="4">4</SelectItem>
                    <SelectItem value="5+">5+</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>

          {/* Valor */}
          <div className="space-y-3">
            <label className="text-sm font-semibold text-neutral-black">Valor (R$)</label>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="number"
                placeholder="Mínimo"
                value={valorMin}
                onChange={(e) => setValorMin(e.target.value)}
                className="w-full min-w-0 px-3 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-neutral-black/10 focus:border-neutral-black transition-all"
              />
              <input
                type="number"
                placeholder="Máximo"
                value={valorMax}
                onChange={(e) => setValorMax(e.target.value)}
                className="w-full min-w-0 px-3 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-neutral-black/10 focus:border-neutral-black transition-all"
              />
            </div>
          </div>

          {/* Area */}
          <div className="space-y-3">
            <label className="text-sm font-semibold text-neutral-black">Área (m²)</label>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="number"
                placeholder="Mínima"
                value={areaMin}
                onChange={(e) => setAreaMin(e.target.value)}
                className="w-full min-w-0 px-3 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-neutral-black/10 focus:border-neutral-black transition-all"
              />
              <input
                type="number"
                placeholder="Máxima"
                value={areaMax}
                onChange={(e) => setAreaMax(e.target.value)}
                className="w-full min-w-0 px-3 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-neutral-black/10 focus:border-neutral-black transition-all"
              />
            </div>
          </div>

          {/* Property Features */}
          <div className="space-y-4">
            <h3 className="text-sm font-bold text-neutral-black">
              Diferenciais do Imóvel
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {propertyFeatures.map((feature) => (
                <label
                  key={feature}
                  className="flex items-center space-x-3 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedPropertyFeatures.includes(feature)}
                    onChange={() => togglePropertyFeature(feature)}
                    className="w-6 h-6 border-2 border-neutral-black rounded"
                  />
                  <span className="text-base text-neutral-black">{feature}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Condominium Features */}
          <div className="space-y-4">
            <h3 className="text-sm font-bold text-neutral-black">
              Diferenciais do Condomínio (Se aplicável)
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {condominiumFeatures.map((feature) => (
                <label
                  key={feature}
                  className="flex items-center space-x-3 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedCondominiumFeatures.includes(feature)}
                    onChange={() => toggleCondominiumFeature(feature)}
                    className="w-6 h-6 border-2 border-neutral-black rounded"
                  />
                  <span className="text-base text-neutral-black">{feature}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex items-center justify-between">
          <button 
            onClick={() => {
              setActiveToggle('Todos');
              setSelectedPropertyTypes([]);
              setSelectedCategories([]);
              setSelectedPropertyFeatures([]);
              setSelectedCondominiumFeatures([]);
              setQuartos('0');
              setSuites('0');
              setBanheiros('0');
              setVagas('0');
              setAreaMin('');
              setAreaMax('');
              onApplyFilters({});
            }}
            className="text-xl text-neutral-black hover:text-neutral-black/80 transition-colors"
          >
            Limpar
          </button>
          <button 
            onClick={handleApplyFilters}
            className="px-8 py-4 bg-neutral-black text-white text-xl rounded-full hover:bg-neutral-black/90 transition-colors"
          >
            Mostrar Imóveis
          </button>
        </div>
      </div>
    </div>
  );
}
